import { useEffect, useMemo, useRef, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { useUser } from '@/contexts/UserContext';
import className from 'classnames';
import '@/pages/StoryViewer/BuildStory.scss';
import { ByzzerTable } from '@/components/ByzzerTable';
import { ByzColDef } from '@/types/TableTypes';
import { FLOATING_DATE_PICKER_FILTER_PARAMS } from '@/constants/table.constants';
import { NavLink } from 'react-router-dom';
import {openCharEditor} from './editor/CharEditor';
import { useTenantApi } from '@/hooks/useTenantApi';
import { confirm, openErrorModal } from '@/components/form/ByzzerModal';
import classnames from 'classnames';
import './CustomCharacteristicsGrid.scss';
import { formatInTimeZone } from 'date-fns-tz';
import Tippy from '@tippyjs/react';
import { CHARACTERISTIC_NAME_TOO_LONG_MODAL_TITLE, DEFAULT_BYZZER_ERROR_MODAL_SUBTITLE, DEFAULT_BYZZER_ERROR_MODAL_TITLE } from '@/components/ByzzerModal/byzzerError.constants';

type CustomCharacteristicsPropsType = {
    setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
}

type CustomCharacteristicsColId = 'characteristics' | 'createdBy' | 'createdDtm' | 'categories' | 'type' | 'actions';

const CustomCharacteristicsGrid = ({ setIsLoading }: CustomCharacteristicsPropsType) => {
    const baseClassName = className('custom-characteristics-table');
    const { customCharacteristics } = useUser();
    const {deleteCustomChar, createDuplicateCustomChar} = useTenantApi();
    const [customCharRowData, setCustomCharRowData] = useState<(Omit<CustomCharacteristic, 'createdDtm'> & {createdDtm: string})[]>([]); 

    useEffect(() => {
        loadCustomChars();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customCharacteristics]);

    function loadCustomChars() {
        const sortedCustomChars = customCharacteristics.sort((a, b) => b.createdDtm.localeCompare(a.createdDtm));            
        setCustomCharRowData(sortedCustomChars.map(item => ({
            ...item,
            createdDtm: formatInTimeZone(new Date(item.createdDtm), 'America/New_York', 'yyyy-MM-dd')
        })));
    }; 

    async function handleEdit(charId: CustomCharacteristic['id']) {
        // @ts-ignore
        if (await openCharEditor({charId})) {
            console.log("Opened Successfully")
        }
    }

    async function handleDelete(idToDelete) {
        
        if (await confirm({
            title: 'Remove Custom Characteristics',
            content: <>
                <p>Are you sure you want to remove this Characteristics?</p>
            </>,
            yesLabel: 'Yes, Remove'
        })) {
            setIsLoading(true);
            try {
                await deleteCustomChar(idToDelete)
            } catch (err) {
                // tbd
            }
        }
        setIsLoading(false);
    }

    async function handleDuplicateClick(charId: CustomCharacteristic['id'], charLabel: CustomCharacteristic['label']) {
        if (
            await confirm({
                title: 'Duplicate Custom Characteristic',
                content: (
                    <>
                        <p>
                            Please confirm you would like to duplicate the <b>{charLabel}</b> custom characteristic.
                        </p>
                    </>
                ),
                yesLabel: 'Yes, Duplicate',
                noLabel: 'Cancel',
            })
        ) {
            setIsLoading(true);
            try {
                await createDuplicateCustomChar(charId);
            } catch (err: any) {
                switch (err?.code) {
                    case 'custom_char_label_too_long':
                        await openErrorModal({
                                title: `Character Limit Exceeded`,
                                content: (
                                    <>
                                        <p>{CHARACTERISTIC_NAME_TOO_LONG_MODAL_TITLE}</p>
                                    </>
                                ),
                                errorId: (err as any)?.id,
                            });
                        break;
                    default:
                        await openErrorModal({
                            title: DEFAULT_BYZZER_ERROR_MODAL_TITLE,
                            content: (
                                <>
                                    <p>{DEFAULT_BYZZER_ERROR_MODAL_SUBTITLE}</p>
                                </>
                            ),
                            errorId: (err as any)?.id,
                        });
                }
            } finally {
                setIsLoading(false);
            }
        }
    }
    
    const gridRef = useRef<AgGridReact>(null);

    const defaultCustomCharacteristicsColDef = useMemo(
        () => ({
            filter: true, // make every column use 'text' filter by default
            sortable: true,
            floatingFilter: true, // enable floating filters by default
            resizable: true, //set each col resizable
            width: 200, // set every column width
            flex: 0, // flex: 1,
            autoHeight: false,
            wrapText: false,
            suppressMenu: true,
            lockVisible: true,
        }),
        []
    );

    const [customCharacteristicsColumnDefs] = useState<ByzColDef<CustomCharacteristic, CustomCharacteristicsColId>[]>([
        {
            colId: 'characteristics',
            headerName: 'Characteristics',
            width: 300,
            valueGetter: ({ data }) => data.label,
            tooltipValueGetter: ({ data }) => data.label,
            pinned: 'left',
        },
        {
            colId: 'createdBy',
            headerName: 'Created By',
            width: 200,
            valueGetter: ({ data }) => {
                return `${data?.createdBy?.firstName} ${data?.createdBy?.lastName}`;
            },
            tooltipValueGetter: ({ data }) => {
                return `${data?.createdBy?.firstName} ${data?.createdBy?.lastName}`;
            },
        },
        {
            ...FLOATING_DATE_PICKER_FILTER_PARAMS,
            headerName: 'Created Date',
            colId: 'createdDtm',
            field:'createdDtm'
        },
        {
            headerName: 'Category(s)',
            colId: 'categories',
            width: 400,
            valueGetter: ({ data }) => data.categories,
            tooltipValueGetter: ({ data }) => data.categories
        },
        {
            headerName: 'Type',
            colId: 'type',
            width: 250,
            valueGetter: ({ data }) => {
                return data.type === 'UPC' ? 'Upload' : 'Characteristic Groups';
            },
            tooltipValueGetter: ({ data }) => {
                return data.type === 'UPC' ? 'Upload' : 'Characteristic Groups';
            },
        },
        {
            headerName: '',
            colId: 'actions',
            suppressMenu: true,
            sortable: false,
            filter: false,
            floatingFilter: false,
            resizable: false,
            pinned: 'right',
            width: 150,
            cellRenderer: (params) => {
                const { data } = params;
                return (
                    <div className={`${baseClassName}__actions`}>
                        <Tippy content={'Edit'} placement={'top'} delay={[500, 0]}>
                            <div className={`${baseClassName}__actions-edit`}>
                                {data.isEditable && (
                                    <span
                                        className={`${baseClassName}__actions-edit-icon`}
                                        onClick={() => handleEdit(data?.id)}
                                    />
                                )}
                            </div>
                        </Tippy>

                        <Tippy content={'Duplicate'} placement={'top'} delay={[500, 0]}>
                            <span
                                className={`${baseClassName}__actions-duplicate-icon`}
                                onClick={() => handleDuplicateClick(data?.id, data?.label)}
                            />
                        </Tippy>
                        {data.isEditable && (
                            <Tippy content={'Delete'} placement={'top'} delay={[500, 0]}>
                                <span
                                    className={`${baseClassName}__actions-delete-icon`}
                                    onClick={() => handleDelete(data?.id)}
                                />
                            </Tippy>
                        )}

                        <NavLink to={`/dashboard/custom_characteristics_report/${data?.id}`}>
                            <Tippy content={'View'} placement={'top'} delay={[500, 0]}>
                                <span className={`${baseClassName}__actions-view-icon`} />
                            </Tippy>
                        </NavLink>
                    </div>
                );
            },
        },
    ]);

    return (
        <>
        <section className={classnames(`${baseClassName}__filters-section`)}>
            <span className={classnames(`${baseClassName}__title`)}>Characteristics created by your team</span>
        </section>
            <ByzzerTable
                ref={gridRef} // Ref for accessing Grid's API
                rowData={customCharRowData} // Row Data for Rows
                columnDefs={customCharacteristicsColumnDefs} // Column Defs for Columns
                defaultColDef={defaultCustomCharacteristicsColDef} // Column Defs for Columns
                tableArea={'customCharacteristics'}
                enableColumnHideShow={true}
                enableSaveLayout={true}
                enableSaveSort={true}
            />
        </>
    );
};

export default CustomCharacteristicsGrid;
